import { ColorPicker } from '../../../../../../../widgets/colorPicker';
import { BasicButton, PrimaryButton } from '../../../../../../../widgets/buttons';
import { Dialog } from '../../../../../../../widgets/dialog';
import { Icon } from '../../../../../../../widgets/icon';
import { FormControl } from '../../../../../../../widgets/formControl';
import { Link } from '../../../../../../../widgets/buttons';
import React, { Suspense } from 'react';
import Select from 'react-select';
import { graphql, useLazyLoadQuery } from 'react-relay';
import type { composerSaveActionDialogPrintTypesQuery } from './__generated__/composerSaveActionDialogPrintTypesQuery.graphql';
import { T } from 'src/shared/helpers';
import PrintIcon from 'src/shared/material_icons/action/print.svg';
import VisibilityIcon from 'src/shared/material_icons/action/visibility.svg';
import VisibilityOffIcon from 'src/shared/material_icons/action/visibility_off.svg';
import ObjectIcon from 'src/shared/material_icons/action/view_in_ar.svg';
import ResetIcon from 'src/shared/material_icons/content/undo.svg';
import CameraIcon from 'src/shared/material_icons/image/camera_alt.svg';
import PivotIcon from 'src/shared/material_icons/image/center_focus_strong.svg';
import DesignIcon from 'src/shared/material_icons/image/brush.svg';
import ColourIcon from 'src/shared/material_icons/image/palette.svg';
import SettingIcon from 'src/shared/material_icons/image/tune.svg';
import PartIcon from 'src/shared/material_icons/maps/category.svg';
import HotspotIcon from 'src/shared/material_icons/maps/pin_drop.svg';
import { useValidationContext, ValidationStateProvider } from 'src/shared/validation';
import styled from 'styled-components';
import {
    ComposerMaterialPropertyType,
    ComposerMaterialsColorType,
    ComposerOptionActionNode,
    ComposerOptionActionsType,
    GetActionValueFromType
} from '../../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { getCapitalizedFormattedName, setActionSceneElementType } from '../../helpers/composerHelpers';
import { ComposerActionIcon, getComposerActionCatalog } from '../../../../configurator-components/composer-editors/attributes/composerActionCatalog';
import { ComposerAttributeNodeAdapter } from '../../helpers/composerNodesAdapter';
import { useMaterialPropertyDetector } from '../../helpers/materialPropertyDetector';
import Stack from '../../../../../../../widgets/stack/stack';
import { ValidationBox } from '../../../../../../../widgets/validation/validationBox';

// SHOW_OBJECTS = 2, // MPlaza.SceneActionType.ShowHideMesh,
// HIDE_OBJECTS = 2, //MPlaza.SceneActionType.ShowHideMesh,
// SET_OBJECT_MATERIAL = 3, //MPlaza.SceneActionType.SetMeshMaterial,
// SET_SUB_OBJECT_MATERIAL = 7, //MPlaza.SceneActionType.SetSubMeshMaterial,
// SET_MATERIAL_COLOR = 4, //MPlaza.SceneActionType.SetMaterialColor,
// MOVE_CAMERA = 5, //MPlaza.SceneActionType.SetCameraLocation,
// SET_DESIGN_MATERIAL = 11, //MPlaza.SceneActionType.SetMeshDesignMaterial,
// SET_CAMERA_PIVOT = 8, //MPlaza.SceneActionType.SetCameraPivot,
// RESET_CAMERA_PIVOT = 9 //MPlaza.SceneActionType.ResetCameraPivot
const ComposerActionsValues = Object.values(ComposerOptionActionsType).filter((value) => typeof value === 'number');
const ComposerActionsTypeArray = ComposerActionsValues as ComposerOptionActionsType[];
const typeSelector = <T,>(type: ComposerOptionActionsType, ...choices: T[]): T => {
    const index = ComposerActionsTypeArray.indexOf(type);
    return choices[index] || choices[0];
};

// Helper function per estrarre il valore corretto dalle action
const getActionValue = (parsedAction: any, actionType: ComposerOptionActionsType): string => {
    const rawValue = parsedAction?.get('value') as string;
    // Con la nuova struttura (dot notation), il valore Ã¨ giÃ  diretto, non serve parsing JSON
    return rawValue || '';
};

const getComposerActionsSelect = () =>
    getComposerActionCatalog().map((definition) => ({
        label: definition.name,
        value: definition.type,
        description: definition.description,
        icon: definition.icon,
        disabled: !!definition.disabled
    }));

const ComposerActionsMaterialColors = Object.entries(ComposerMaterialsColorType).map((key) => {
    return { label: key[0], value: key[1] };
});

const printTypesQuery = graphql`
    query composerSaveActionDialogPrintTypesQuery {
        printTypes(first: 200) {
            edges {
                node {
                    id
                    printTypeID
                    name
                }
            }
        }
    }
`;

// The configurator product no longer owns a single printing method: the active PM is chosen here, per attribute option,
// and applied at runtime by the SetPrintingMethod scene action. Options are the account-level printing methods.
const PrintingMethodSelect: React.FC<{ value: string; onChange: (printTypeID: string) => void }> = ({ value, onChange }) => {
    const data = useLazyLoadQuery<composerSaveActionDialogPrintTypesQuery>(printTypesQuery, {});
    const options = React.useMemo(
        () =>
            (data?.printTypes?.edges ?? []).map((edge) => ({
                value: String(edge?.node?.printTypeID),
                label: edge?.node?.name || String(edge?.node?.printTypeID)
            })),
        [data]
    );
    return (
        <Select
            options={options}
            value={options.find((option) => option.value === value) || null}
            onChange={(selected) => onChange(selected?.value || '')}
            menuPosition='fixed'
        />
    );
};
const ComposerSaveOptionDialogWindow = styled(Dialog)`
    max-width: 600px;
    position: static;
    overflow-y: show;
`;

const ComposerSaveOptionContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    // min-height: 500px;
    overflow-y: show;
    align-items: center;
    padding: 2em;
    justify-content: start;
    position: static;
`;

const StyledFormControl = styled(FormControl)`
    overflow: visible;
`;

const ActionTypeOption = styled.div<{ $disabled?: boolean }>`
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr);
    align-items: center;
    gap: 9px;
    opacity: ${(props) => (props.$disabled ? 0.55 : 1)};

    > ${Icon} {
        width: 20px;
        height: 20px;
        color: ${(props) => props.theme.app.secondary};
    }

    svg {
        width: 20px;
        height: 20px;
        fill: currentColor;
    }

    div {
        display: flex;
        min-width: 0;
        flex-direction: column;
        gap: 2px;
    }

    strong {
        color: ${(props) => props.theme.app.textColor};
        font-size: 13px;
    }

    span {
        color: ${(props) => props.theme.app.primary};
        font-size: 11px;
        line-height: 15px;
    }
`;

const getActionIcon = (icon: ComposerActionIcon) => {
    const glyph =
        icon === 'visibility' ? (
            <VisibilityIcon />
        ) : icon === 'visibilityOff' ? (
            <VisibilityOffIcon />
        ) : icon === 'object' ? (
            <ObjectIcon />
        ) : icon === 'part' ? (
            <PartIcon />
        ) : icon === 'colour' ? (
            <ColourIcon />
        ) : icon === 'camera' ? (
            <CameraIcon />
        ) : icon === 'design' ? (
            <DesignIcon />
        ) : icon === 'pivot' ? (
            <PivotIcon />
        ) : icon === 'reset' ? (
            <ResetIcon />
        ) : icon === 'setting' ? (
            <SettingIcon />
        ) : icon === 'print' ? (
            <PrintIcon />
        ) : (
            <HotspotIcon />
        );
    return <Icon>{glyph}</Icon>;
};

const InlineActionEditor = styled.div`
    margin-top: 6px;
    background: ${(props) => props.theme.app.pageBackgroundColor};
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: ${(props) => props.theme.app.component.card.borderRadius};

    ${ComposerSaveOptionContainer} {
        align-items: stretch;
        height: auto;
        padding: 12px;
    }
`;

const InlineActionButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 0 12px 12px;
`;

interface ComposerSaveActionProps {
    onConfirm: (action: ComposerOptionActionNode | null) => void;
    passedAction?: ComposerOptionActionNode | null;
    optionID: string;
    attributeID: string;
    materials?: BABYLON.Material[];
    meshes?: BABYLON.AbstractMesh[];
    cameras?: BABYLON.Camera[];
    edit?: boolean;
    embedded?: boolean;
    onCancel?: () => void;
}

interface MaterialActionValidationValue {
    actionType: ComposerOptionActionsType;
    selectedMaterial: BABYLON.Material | null;
    selectedProperty: string;
    selectedSubProperty: { value: string; type: ComposerMaterialPropertyType; label?: string } | null;
    selectedSubSubProperty: { value: string; type: ComposerMaterialPropertyType; label?: string } | null;
    propertyName: string;
    requiresSubSubProperty: boolean;
    currentValue: string;
}

const validateMaterialAction = (value: MaterialActionValidationValue) => {
    if (value.actionType !== ComposerOptionActionsType.SET_MATERIAL_PARAMETER) return false;

    return (
        !value.selectedMaterial ||
        !value.selectedProperty ||
        !value.selectedSubProperty ||
        (value.requiresSubSubProperty && !value.selectedSubSubProperty) ||
        !value.propertyName ||
        (!value.currentValue && value.currentValue !== '')
    );
};

const getMaterialActionValidationMessage = (value: MaterialActionValidationValue) => {
    if (!value.selectedMaterial) return T._('Material is required.', 'Admin');
    if (!value.selectedProperty) return T._('Property group is required.', 'Admin');
    if (!value.selectedSubProperty) return T._('Sub-property is required.', 'Admin');
    if (value.requiresSubSubProperty && !value.selectedSubSubProperty) return T._('Sub-sub-property is required.', 'Admin');
    if (!value.propertyName) return T._('Property name is not set. Reselect the property.', 'Admin');
    return T._('Property value is required.', 'Admin');
};

const ComposerSaveActionDialogContent: React.FC<ComposerSaveActionProps> = ({
    onConfirm,
    passedAction,
    optionID,
    attributeID,
    edit,
    embedded,
    onCancel,
    meshes,
    materials,
    cameras
}) => {
    const validationContext = useValidationContext();
    const [parsedAction, setParsedAction] = React.useState<MPlaza.AttributeSceneAction>(ComposerAttributeNodeAdapter.fromActionNode(passedAction || null));
    const [actionType, setActionType] = React.useState<ComposerOptionActionsType>(passedAction?.type ?? ComposerOptionActionsType.SHOW_OBJECTS);

    // Stati per SET_MATERIAL_PARAMETER
    const [selectedMaterial, setSelectedMaterial] = React.useState<BABYLON.Material | null>(() => {
        if (passedAction?.type === ComposerOptionActionsType.SET_MATERIAL_PARAMETER) {
            const materialId = parsedAction?.get('selector');
            return materials?.find((material) => material.id === materialId) || null;
        }
        return null;
    });

    // Stati per la selezione delle proprietÃ
    const [selectedProperty, setSelectedProperty] = React.useState<string>('');
    const [selectedSubProperty, setSelectedSubProperty] = React.useState<{ value: string; type: ComposerMaterialPropertyType; label?: string } | null>(null);
    const [selectedSubSubProperty, setSelectedSubSubProperty] = React.useState<{ value: string; type: ComposerMaterialPropertyType; label?: string } | null>(
        null
    );
    const [propertyName, setPropertyName] = React.useState<string>('');

    // Usa l'hook MaterialPropertyDetector per ottenere tutte le proprietÃ  disponibili e leggere i valori reali
    const materialPropertyHook = useMaterialPropertyDetector(selectedMaterial);
    const { getAvailablePropertyGroups, renderPropertyControl, hasSubSubProperties, getSubSubProperties } = materialPropertyHook;

    // Flag per controllare che l'inizializzazione avvenga solo UNA VOLTA all'apertura della dialog
    const hasInitializedFromAction = React.useRef(false);

    // Ricostruisci dinamicamente gli stati quando editi un'action esistente
    // IMPORTANTE: Questo useEffect deve eseguire SOLO all'apertura della dialog, non ad ogni cambio
    React.useEffect(() => {
        console.log('ðŸ”µ useEffect triggered', {
            hasInitialized: hasInitializedFromAction.current,
            hasPassedAction: !!passedAction,
            actionType: passedAction?.type,
            hasParsedAction: !!parsedAction,
            hasMaterial: !!selectedMaterial,
            propertyGroupsCount: getAvailablePropertyGroups.length
        });

        // Se abbiamo giÃ  inizializzato, non fare nulla
        if (hasInitializedFromAction.current) {
            console.log('â­ï¸ Already initialized, skipping');
            return;
        }

        if (passedAction?.type === ComposerOptionActionsType.SET_MATERIAL_PARAMETER && parsedAction && selectedMaterial) {
            const propertyPath = parsedAction.get('propertyName') as string;

            if (!propertyPath || !getAvailablePropertyGroups || getAvailablePropertyGroups.length === 0) {
                console.log('âš ï¸ Cannot initialize: missing propertyPath or no property groups available', {
                    propertyPath,
                    groupsLength: getAvailablePropertyGroups?.length
                });
                return;
            }

            console.log('ðŸ”„ Initializing action from propertyPath:', propertyPath);
            console.log('ðŸ“‹ Available property groups:', getAvailablePropertyGroups.length);

            // Parse della dot notation per ricostruire le selezioni
            // Formato NORMALIZZATO: "{propertyGroup}.{subProperty}" o "{propertyGroup}.{subProperty}.{subSubProperty}"
            // Esempi:
            //   - "albedo.albedoColor"
            //   - "clearCoat.useRoughnessFromMainTexture"
            //   - "anisotropic.texture"
            //   - "subsurface.thicknessTexture"
            // Formato LEGACY (con underscore):
            //   - "clearCoat_textureRoughnessTexture"
            //   - "anisotropy_texture"

            let parsedPropertyGroup = '';
            let parsedSubProperty = '';

            // Se contiene un punto, Ã¨ il formato con dot notation
            if (propertyPath.includes('.')) {
                const parts = propertyPath.split('.');
                parsedPropertyGroup = parts[0].toLowerCase(); // normalizza a lowercase
                parsedSubProperty = parts.slice(1).join('.'); // resto del path
                console.log('ðŸ”§ Parsed dot notation:', {
                    parsedPropertyGroup,
                    parsedSubProperty,
                    fullPath: propertyPath,
                    parts: parts
                });
            } else if (propertyPath.includes('_')) {
                // Formato legacy con underscore: "{group}_{property}"
                const parts = propertyPath.split('_');
                parsedPropertyGroup = parts[0].toLowerCase(); // es. "clearCoat" â†’ "clearcoat"
                parsedSubProperty = parts.slice(1).join('_'); // resto con underscore
                console.log('ðŸ”§ Parsed legacy underscore format:', {
                    parsedPropertyGroup,
                    parsedSubProperty,
                    fullPath: propertyPath
                });
            }

            // Cerca in tutti i property groups disponibili
            for (const propGroup of getAvailablePropertyGroups) {
                console.log(`  ðŸ” Checking property group: ${propGroup.value}`);

                // Se abbiamo parsato il property group, cerca solo quello specifico
                if (parsedPropertyGroup && propGroup.value.toLowerCase() !== parsedPropertyGroup.toLowerCase()) {
                    continue;
                }

                // Cerca nelle sub-properties
                if (propGroup.subProperties) {
                    for (const subProp of propGroup.subProperties) {
                        // Match con dot notation: confronta la parte dopo il punto
                        if (parsedSubProperty && subProp.value === parsedSubProperty) {
                            console.log(`  âœ… Found match via dot notation: ${propGroup.value}.${subProp.value}`);
                            setSelectedProperty(propGroup.value);
                            setSelectedSubProperty(subProp);
                            setPropertyName(parsedSubProperty); // Salva solo la sub-property per compatibilitÃ  UI
                            hasInitializedFromAction.current = true;
                            return;
                        }

                        // Match diretto sulla subProperty (formato legacy)
                        if (subProp.value === propertyPath) {
                            console.log(`  âœ… Found direct match in subProperty: ${subProp.value}`);
                            setSelectedProperty(propGroup.value);
                            setSelectedSubProperty(subProp);
                            setPropertyName(propertyPath);
                            hasInitializedFromAction.current = true;
                            return;
                        }

                        // Se ha sub-sub-properties, cerca anche lÃ¬
                        if (hasSubSubProperties(propGroup.value, subProp.value)) {
                            const subSubProps = getSubSubProperties(propGroup.value, subProp.value);
                            console.log(`    ðŸ” Checking ${subSubProps.length} sub-sub-properties under ${propGroup.value}.${subProp.value}`);
                            console.log(`    ðŸ” Looking for propertyPath: "${propertyPath}" or parsedSubProperty: "${parsedSubProperty}"`);

                            for (const subSubProp of subSubProps) {
                                // Per dot notation: costruisci il full path della sub-sub-property e confronta
                                // Esempio: propertyPath = "clearcoat.useRoughnessFromMainTexture"
                                //          subSubProp.value potrebbe essere "clearCoat.useRoughnessFromMainTexture"
                                //          oppure solo "useRoughnessFromMainTexture"

                                // Match con il full path originale
                                if (subSubProp.value === propertyPath) {
                                    setSelectedProperty(propGroup.value);
                                    setSelectedSubProperty(subProp);
                                    setSelectedSubSubProperty(subSubProp);
                                    setPropertyName(parsedSubProperty); // Salva solo la parte dopo il punto
                                    hasInitializedFromAction.current = true;
                                    return;
                                } // Match con la parte dopo il punto (parsedSubProperty)
                                // Questo gestisce il caso dove la sub-sub-property contiene la dot notation completa
                                // es. subSubProp.value = "clearCoat.useRoughnessFromMainTexture" e parsedSubProperty = "useRoughnessFromMainTexture"
                                if (parsedSubProperty && subSubProp.value.includes('.')) {
                                    const subSubPropParts = subSubProp.value.split('.');
                                    const subSubPropName = subSubPropParts[subSubPropParts.length - 1]; // ultima parte
                                    if (subSubPropName === parsedSubProperty) {
                                        setSelectedProperty(propGroup.value);
                                        setSelectedSubProperty(subProp);
                                        setSelectedSubSubProperty(subSubProp);
                                        setPropertyName(parsedSubProperty);
                                        hasInitializedFromAction.current = true;
                                        return;
                                    }
                                }

                                // Match semplice con parsedSubProperty per backward compatibility
                                if (parsedSubProperty && subSubProp.value === parsedSubProperty) {
                                    setSelectedProperty(propGroup.value);
                                    setSelectedSubProperty(subProp);
                                    setSelectedSubSubProperty(subSubProp);
                                    setPropertyName(parsedSubProperty);
                                    hasInitializedFromAction.current = true;
                                    return;
                                }
                            }
                        }
                    }
                }
            }

            // CASI SPECIALI: Gestione texture per clearCoat, sheen, anisotropic, subSurface

            // ==================== CLEARCOAT ROUGHNESS ====================
            const isClearCoatRoughness =
                propertyPath === 'clearCoat.useRoughnessFromMainTexture' ||
                propertyPath === 'clearCoat_textureRoughnessTexture' ||
                propertyPath === 'clearcoat.useRoughnessFromMainTexture' ||
                propertyPath === 'clearcoat.clearCoat.useRoughnessFromMainTexture' ||
                (propertyPath.includes('clearCoat') && propertyPath.includes('useRoughnessFromMainTexture')) ||
                (propertyPath.includes('clearCoat') && propertyPath.includes('RoughnessTexture'));

            if (isClearCoatRoughness) {
                const clearCoatGroup = getAvailablePropertyGroups.find((pg) => pg.value === 'clearcoat');
                if (clearCoatGroup) {
                    const roughnessSection = clearCoatGroup.subProperties.find((sp) => sp.value === 'clearCoat_roughness_section');
                    if (roughnessSection) {
                        setSelectedProperty('clearcoat');
                        setSelectedSubProperty(roughnessSection);

                        const subSubProps = getSubSubProperties('clearcoat', 'clearCoat_roughness_section');
                        const checkboxOption = subSubProps.find((ssp) => ssp.value === 'clearCoat.useRoughnessFromMainTexture');
                        if (checkboxOption) {
                            setSelectedSubSubProperty(checkboxOption);
                        }

                        const finalPropertyName = parsedSubProperty || propertyPath;
                        setPropertyName(finalPropertyName);

                        const actionValue = parsedAction?.get('value') as string;
                        if (propertyPath === 'clearCoat.useRoughnessFromMainTexture') {
                            setUseRoughnessFromMainTexture(actionValue === 'true');
                        } else {
                            setUseRoughnessFromMainTexture(false);
                        }

                        hasInitializedFromAction.current = true;
                        console.log('âœ… Initialized clearCoat roughness from action');
                        return;
                    }
                }
            }

            // ==================== SHEEN ROUGHNESS/TEXTURE ====================
            const isSheenProperty =
                propertyPath.includes('sheen') &&
                (propertyPath.includes('roughness') ||
                    propertyPath.includes('texture') ||
                    propertyPath.includes('useRoughnessFromMainTexture') ||
                    propertyPath.includes('Roughness'));

            if (isSheenProperty) {
                const sheenGroup = getAvailablePropertyGroups.find((pg) => pg.value === 'sheen');
                if (sheenGroup) {
                    // Cerca la sub-property appropriata
                    let targetSubProp = null;

                    // Se Ã¨ una texture generica sheen
                    if (propertyPath.includes('sheen_texture') || propertyPath.includes('sheen.texture')) {
                        targetSubProp = sheenGroup.subProperties.find((sp) => sp.value === 'sheen_texture');
                    }
                    // Se Ã¨ roughness texture o checkbox
                    else if (propertyPath.includes('roughness') || propertyPath.includes('Roughness')) {
                        // Cerca una section per roughness (simile a clearCoat)
                        targetSubProp = sheenGroup.subProperties.find((sp) => sp.value.includes('roughness') || sp.value.includes('Roughness'));
                    }

                    if (targetSubProp) {
                        setSelectedProperty('sheen');
                        setSelectedSubProperty(targetSubProp);

                        // Se ha sub-sub-properties, cerca quella corretta
                        if (hasSubSubProperties('sheen', targetSubProp.value)) {
                            const subSubProps = getSubSubProperties('sheen', targetSubProp.value);
                            const matchingSubSub = subSubProps.find(
                                (ssp) =>
                                    ssp.value === propertyPath ||
                                    ssp.value === parsedSubProperty ||
                                    (parsedSubProperty && ssp.value.includes(parsedSubProperty))
                            );
                            if (matchingSubSub) {
                                setSelectedSubSubProperty(matchingSubSub);
                            }
                        }

                        const finalPropertyName = parsedSubProperty || propertyPath;
                        setPropertyName(finalPropertyName);

                        hasInitializedFromAction.current = true;
                        console.log('âœ… Initialized sheen property from action:', propertyPath);
                        return;
                    }
                }
            }

            // ==================== ANISOTROPIC TEXTURE ====================
            const isAnisotropicTexture =
                (propertyPath.includes('anisotropy') && propertyPath.includes('texture')) ||
                (propertyPath.includes('anisotropic') && propertyPath.includes('texture')) ||
                propertyPath === 'anisotropy_texture' ||
                propertyPath === 'anisotropic.texture';

            if (isAnisotropicTexture) {
                const anisotropicGroup = getAvailablePropertyGroups.find((pg) => pg.value === 'anisotropic' || pg.value === 'anisotropy');
                if (anisotropicGroup) {
                    const textureSubProp = anisotropicGroup.subProperties.find((sp) => sp.value.includes('texture') || sp.value === 'anisotropy_texture');
                    if (textureSubProp) {
                        setSelectedProperty(anisotropicGroup.value);
                        setSelectedSubProperty(textureSubProp);

                        const finalPropertyName = parsedSubProperty || propertyPath;
                        setPropertyName(finalPropertyName);

                        hasInitializedFromAction.current = true;
                        console.log('âœ… Initialized anisotropic texture from action');
                        return;
                    }
                }
            }

            // ==================== SUBSURFACE TEXTURES ====================
            const isSubSurfaceTexture =
                (propertyPath.includes('subSurface') && propertyPath.includes('texture')) ||
                (propertyPath.includes('subsurface') && propertyPath.includes('Texture')) ||
                (propertyPath.startsWith('subSurface_') && propertyPath.includes('Texture'));

            if (isSubSurfaceTexture) {
                const subSurfaceGroup = getAvailablePropertyGroups.find((pg) => pg.value === 'subsurface' || pg.value === 'subSurface');
                if (subSurfaceGroup) {
                    // Cerca la sub-property che corrisponde alla texture specifica
                    // Es: subSurface_thicknessTexture, subSurface_refractionIntensityTexture, etc.
                    const textureSubProp = subSurfaceGroup.subProperties.find((sp) => {
                        // Match diretto
                        if (sp.value === propertyPath || sp.value === parsedSubProperty) {
                            return true;
                        }
                        // Match parziale - la sub-property contiene il nome della texture
                        if (parsedSubProperty && sp.value.includes(parsedSubProperty)) {
                            return true;
                        }
                        // Match per formato legacy con underscore
                        const legacyName = propertyPath.replace('.', '_');
                        if (sp.value === legacyName) {
                            return true;
                        }
                        return false;
                    });

                    if (textureSubProp) {
                        setSelectedProperty(subSurfaceGroup.value);
                        setSelectedSubProperty(textureSubProp);

                        const finalPropertyName = parsedSubProperty || propertyPath;
                        setPropertyName(finalPropertyName);

                        hasInitializedFromAction.current = true;
                        console.log('âœ… Initialized subSurface texture from action:', propertyPath);
                        return;
                    }
                }
            }
            getAvailablePropertyGroups.forEach((pg) => {
                pg.subProperties.forEach((sp) => {
                    console.log(`  - ${sp.value}`);
                    if (hasSubSubProperties(pg.value, sp.value)) {
                        getSubSubProperties(pg.value, sp.value).forEach((ssp) => {
                            console.log(`    - ${ssp.value}`);
                        });
                    }
                });
            });
        }
    }, [passedAction, parsedAction, selectedMaterial, getAvailablePropertyGroups, hasSubSubProperties, getSubSubProperties]);

    // Flag per la roughness checkbox - inizializza dal valore del materiale o dall'azione salvata
    const [useRoughnessFromMainTexture, setUseRoughnessFromMainTexture] = React.useState<boolean>(() => {
        // Se stiamo editando un'azione esistente
        if (passedAction?.type === ComposerOptionActionsType.SET_MATERIAL_PARAMETER) {
            const propertyPath = parsedAction?.get('propertyName') as string;
            const value = parsedAction?.get('value') as string;

            // Se l'azione salvata Ã¨ per useRoughnessFromMainTexture, usa quel valore
            if (propertyPath === 'clearCoat.useRoughnessFromMainTexture') {
                return value === 'true';
            }

            // Se l'azione salvata Ã¨ per la TEXTURE, allora useRoughnessFromMainTexture Ã¨ FALSE
            if (propertyPath === 'clearCoat_textureRoughnessTexture') {
                return false;
            }
        }

        // Altrimenti leggi il valore attuale dal materiale selezionato
        if (selectedMaterial) {
            try {
                const material = selectedMaterial as any;
                if (material.clearCoat && typeof material.clearCoat.useRoughnessFromMainTexture !== 'undefined') {
                    return material.clearCoat.useRoughnessFromMainTexture;
                }
            } catch (e) {
                console.warn('Could not read clearCoat.useRoughnessFromMainTexture from material:', e);
            }
        }

        return true; // Default value
    });

    const handleConfirm = () => {
        if (!validationContext.submit()) return;

        // Validazione per SET_MATERIAL_PARAMETER
        if (actionType === ComposerOptionActionsType.SET_MATERIAL_PARAMETER) {
            const currentValue = getActionValue(parsedAction, actionType);

            // Imposta i valori finali nella parsedAction prima di salvare
            // NUOVA STRUTTURA: usa dot notation nel propertyName e solo il valore in value
            parsedAction?.set('selector', selectedMaterial!.id);

            // Costruisci il propertyName completo con dot notation NORMALIZZATO
            // Formato finale: "{selectedProperty}.{actualPropertyName}"
            // Es: "clearcoat.useRoughnessFromMainTexture", "anisotropic.texture", "subsurface.thicknessTexture"

            let fullPropertyName = '';

            // Se propertyName contiene giÃ  un punto, potrebbe avere il prefisso duplicato
            if (propertyName.includes('.')) {
                // Rimuovi il prefisso selectedProperty se Ã¨ duplicato
                // Es: se propertyName = "clearcoat.useRoughnessFromMainTexture" e selectedProperty = "clearcoat"
                //     oppure propertyName = "clearCoat.useRoughnessFromMainTexture" (case diverso)
                const normalizedPropertyName = propertyName.toLowerCase();
                const normalizedSelectedProperty = selectedProperty?.toLowerCase() || '';

                if (normalizedPropertyName.startsWith(normalizedSelectedProperty + '.')) {
                    // Il propertyName ha giÃ  il prefisso â†’ usa as-is
                    fullPropertyName = propertyName;
                } else {
                    // Il propertyName ha un punto ma NON Ã¨ il selectedProperty â†’ concatena
                    fullPropertyName = selectedProperty ? `${selectedProperty}.${propertyName}` : propertyName;
                }
            } else {
                // propertyName NON ha il punto â†’ concatena con selectedProperty
                fullPropertyName = selectedProperty ? `${selectedProperty}.${propertyName}` : propertyName;
            }

            parsedAction?.set('propertyName', fullPropertyName); // dot notation normalizzata
            parsedAction?.set('value', currentValue); // solo il valore: es. "#ffffff", "true", "https://texture.jpg"

            // CRITICAL: Assicurati che actionTypeID sia impostato correttamente

            const correctActionTypeID = GetActionValueFromType(ComposerOptionActionsType.SET_MATERIAL_PARAMETER);

            parsedAction?.set('actionTypeID', correctActionTypeID);

            console.log('ðŸ’¾ Saving SET_MATERIAL_PARAMETER action:', {
                actionTypeID: correctActionTypeID,
                selector: selectedMaterial!.id,
                propertyName: fullPropertyName,
                originalPropertyName: propertyName,
                selectedProperty: selectedProperty,
                value: currentValue,
                parsedActionJSON: parsedAction?.toJSON()
            });
        }

        setActionSceneElementType(parsedAction);

        const finalNodeAction = ComposerAttributeNodeAdapter.toNodeAction(attributeID, optionID, parsedAction);
        console.log('ðŸ’¾ Final node action before confirm:', {
            type: finalNodeAction.type,
            typeName: ComposerOptionActionsType[finalNodeAction.type],
            selector: finalNodeAction.selector,
            property: finalNodeAction.property,
            value: finalNodeAction.value
        });
        onConfirm(finalNodeAction);
    };

    const title = edit ? T._('Edit Action', 'Admin') : T._('Create New Action', 'Admin');

    const setSelector = (value: string | undefined | null) => {
        parsedAction?.set('selector', value);
    };

    const updatePropertyName = (value: string | undefined | null) => {
        const newValue = value || '';
        setPropertyName(newValue);
        parsedAction?.set('propertyName', value);
    };

    const setValue = (value: string | undefined | null) => {
        // Logica speciale per roughness checkbox
        if (propertyName === 'clearCoat.useRoughnessFromMainTexture') {
            setUseRoughnessFromMainTexture(value === 'true');
        }

        // Per SET_MATERIAL_PARAMETER, salva solo il valore (non piÃ¹ JSON)
        parsedAction?.set('value', value || '');
    };

    const update = () => {
        setParsedAction((parsedAction) => parsedAction.clone());
    };

    const form = () => {
        switch (actionType) {
            case ComposerOptionActionsType.SHOW_OBJECTS:
                //SHOW OBJECT
                return (
                    <>
                        <StyledFormControl label={T._('Show Objects', 'Admin')}>
                            <Select
                                isMulti
                                options={
                                    meshes?.map((mesh) => ({
                                        value: mesh.id,
                                        label: mesh.name || T._('Mesh', 'Admin')
                                    })) ?? []
                                }
                                value={
                                    parsedAction
                                        ?.get('selector')
                                        ?.split(';')
                                        .map((s) => ({ value: s, label: meshes?.find((m) => m.id === s)?.name })) || []
                                }
                                onChange={(selected) => {
                                    setSelector(
                                        selected
                                            .filter((s) => s.value.trim())
                                            .map((s) => s.value)
                                            .join(';')
                                    );
                                    setValue('show');
                                    update();
                                }}
                                menuPosition='fixed'
                            />
                        </StyledFormControl>
                    </>
                );
            case ComposerOptionActionsType.HIDE_OBJECTS:
                return (
                    <>
                        <FormControl label={T._('Hide Objects', 'Admin')}>
                            <Select
                                isMulti
                                options={
                                    meshes?.map((mesh) => ({
                                        value: mesh.id,
                                        label: mesh.name || mesh.id
                                    })) ?? []
                                }
                                value={
                                    parsedAction
                                        ?.get('selector')
                                        ?.split(';')
                                        .map((s) => ({ value: s, label: meshes?.find((m) => m.id === s)?.name })) || []
                                }
                                onChange={(selected) => {
                                    setSelector(
                                        selected

                                            .filter((s) => s.value.trim())
                                            .map((s) => s.value)
                                            .join(';')
                                    );
                                    setValue('hide');
                                    update();
                                }}
                                menuPosition='fixed'
                            />
                        </FormControl>
                    </>
                );
            case ComposerOptionActionsType.SET_OBJECT_MATERIAL:
                return (
                    <>
                        <Stack>
                            <FormControl label={T._('Objects', 'Admin')}>
                                <Select
                                    isMulti
                                    options={
                                        meshes?.map((mesh) => ({
                                            value: mesh.id,
                                            label: mesh.name || mesh.id
                                        })) ?? []
                                    }
                                    value={
                                        parsedAction
                                            ?.get('selector')
                                            ?.split(';')
                                            .map((s) => ({ value: s, label: meshes?.find((m) => m.id === s)?.name })) || []
                                    }
                                    onChange={(selected) => {
                                        setSelector(
                                            selected
                                                .filter((s) => s.value.trim())
                                                .map((s) => s.value)
                                                .join(';')
                                        );
                                        update();
                                    }}
                                    menuPosition='fixed'
                                />
                            </FormControl>
                            <FormControl label={T._('Material', 'Admin')}>
                                <Select
                                    options={
                                        materials?.map((material) => ({
                                            value: material.id,
                                            label: material.name || material.id
                                        })) ?? []
                                    }
                                    value={{
                                        value: (parsedAction?.get('value') as string) || '',
                                        label: materials?.find((m) => m.id === parsedAction?.get('value'))?.name || (parsedAction?.get('value') as string) || ''
                                    }}
                                    onChange={(selected) => {
                                        setValue(selected?.value || '');
                                        update();
                                    }}
                                    menuPosition='fixed'
                                />
                            </FormControl>
                        </Stack>
                    </>
                );
            case ComposerOptionActionsType.SET_SUB_OBJECT_MATERIAL:
                return (
                    <>
                        <Stack>
                            <FormControl label={T._('Objects', 'Admin')}>
                                <Select
                                    isMulti
                                    options={
                                        meshes?.map((mesh) => ({
                                            value: mesh.id,
                                            label: mesh.name || mesh.id
                                        })) ?? []
                                    }
                                    value={
                                        parsedAction
                                            ?.get('selector')
                                            ?.split(';')
                                            .map((s) => ({ value: s, label: meshes?.find((m) => m.id === s)?.name || s })) || []
                                    }
                                    onChange={(selected) => {
                                        setSelector(
                                            selected
                                                .filter((s) => s.value.trim())
                                                .map((s) => s.value)
                                                .join(';')
                                        );
                                        update();
                                    }}
                                    menuPosition='fixed'
                                />
                            </FormControl>
                            <FormControl label={T._('Material', 'Admin')}>
                                <Select
                                    options={
                                        materials?.map((material) => ({
                                            value: material.id,
                                            label: material.name || material.id
                                        })) ?? []
                                    }
                                    value={{
                                        value: (parsedAction?.get('value') as string) || '',
                                        label: materials?.find((m) => m.id === parsedAction?.get('value'))?.name || (parsedAction?.get('value') as string) || ''
                                    }}
                                    onChange={(selected) => {
                                        setValue(selected?.value || '');
                                        update();
                                    }}
                                    menuPosition='fixed'
                                />
                            </FormControl>
                        </Stack>
                    </>
                );
            case ComposerOptionActionsType.SET_MATERIAL_COLOR:
                return (
                    <>
                        <Stack>
                            <FormControl label={T._('Materials', 'Admin')}>
                                <Select
                                    isMulti
                                    options={
                                        materials?.map((material) => ({
                                            value: material.id,
                                            label: material.name || material.id
                                        })) ?? []
                                    }
                                    value={
                                        parsedAction
                                            ?.get('selector')
                                            ?.split(';')
                                            .map((s) => ({ value: s, label: materials?.find((m) => m.id === s)?.name || s })) || []
                                    }
                                    onChange={(selected) => {
                                        setSelector(
                                            selected
                                                .filter((s) => s.value.trim())
                                                .map((s) => s.value)
                                                .join(';')
                                        );
                                        update();
                                    }}
                                    menuPosition='fixed'
                                />
                            </FormControl>
                            <FormControl label={T._('Color type', 'Admin')}>
                                <Select
                                    options={ComposerActionsMaterialColors}
                                    value={ComposerActionsMaterialColors.find((option) => option.value === parsedAction?.get('propertyName')) || null}
                                    onChange={(selected) => {
                                        updatePropertyName(selected?.value || '');
                                        update();
                                    }}
                                    menuPosition='fixed'
                                />
                            </FormControl>
                            <FormControl label={T._('Color value', 'Admin')}>
                                <ColorPicker
                                    color={getActionValue(parsedAction, actionType)}
                                    onChange={(color) => {
                                        setValue(color);
                                        update();
                                    }}
                                />
                            </FormControl>
                        </Stack>
                    </>
                );
            case ComposerOptionActionsType.SET_PRINTING_METHOD:
                return (
                    <>
                        <FormControl label={T._('Printing method', 'Admin')}>
                            <Suspense fallback={<Select isDisabled placeholder={T._('Loading…', 'Admin')} options={[]} menuPosition='fixed' />}>
                                <PrintingMethodSelect
                                    value={(parsedAction?.get('value') as string) || ''}
                                    onChange={(printTypeID) => {
                                        setValue(printTypeID);
                                        update();
                                    }}
                                />
                            </Suspense>
                        </FormControl>
                    </>
                );
            case ComposerOptionActionsType.SET_MATERIAL_PARAMETER:
                return (
                    <>
                        {/* STEP 1: Selezione Materiale */}
                        <FormControl label={T._('Material', 'Admin')}>
                            <Select
                                options={
                                    materials?.map((material) => ({
                                        value: material.id,
                                        label: material.name || material.id
                                    })) ?? []
                                }
                                value={
                                    selectedMaterial
                                        ? {
                                              value: selectedMaterial.id,
                                              label: selectedMaterial.name || selectedMaterial.id
                                          }
                                        : null
                                }
                                onChange={(selected) => {
                                    const material = materials?.find((m) => m.id === selected?.value) || null;
                                    setSelectedMaterial(material);
                                    setSelectedProperty(''); // Reset property selection
                                    setSelectedSubProperty(null); // Reset sub-property selection
                                    setSelectedSubSubProperty(null); // Reset sub-sub-property selection
                                    setValue('');
                                    setSelector(selected?.value || '');

                                    // Aggiorna useRoughnessFromMainTexture leggendo dal materiale
                                    if (material) {
                                        try {
                                            const mat = material as any;
                                            if (mat.clearCoat && typeof mat.clearCoat.useRoughnessFromMainTexture !== 'undefined') {
                                                setUseRoughnessFromMainTexture(mat.clearCoat.useRoughnessFromMainTexture);
                                            }
                                        } catch (e) {
                                            console.warn('Could not read clearCoat.useRoughnessFromMainTexture:', e);
                                        }
                                    }

                                    update();
                                }}
                                placeholder={T._('Select a material...', 'Admin')}
                                menuPosition='fixed'
                            />
                        </FormControl>
                        {/* STEP 2: Selezione ProprietÃ  (solo se materiale selezionato) */}
                        {selectedMaterial && (
                            <FormControl label={T._('Property', 'Admin')}>
                                <Select
                                    options={getAvailablePropertyGroups}
                                    value={getAvailablePropertyGroups.find((prop) => prop.value === selectedProperty) || null}
                                    getOptionLabel={(option) => option.label}
                                    getOptionValue={(option) => option.value}
                                    isDisabled={getAvailablePropertyGroups.length === 0}
                                    onChange={(selected) => {
                                        const value = selected?.value || '';
                                        setSelectedProperty(value);
                                        setSelectedSubProperty(null);
                                        setSelectedSubSubProperty(null);
                                        setValue('');
                                    }}
                                    placeholder={T._('Select a property...', 'Admin')}
                                    menuPosition='fixed'
                                />
                                {getAvailablePropertyGroups.length === 0 && (
                                    <div style={{ color: '#ff6b35', fontSize: '12px', marginTop: '4px' }}>
                                        {T._('No properties detected for this material', 'Admin')}
                                    </div>
                                )}
                                {getAvailablePropertyGroups.length > 0 && !selectedProperty && (
                                    <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                                        {T._('Found', 'Admin')} {getAvailablePropertyGroups.length} {T._('properties available', 'Admin')}
                                    </div>
                                )}
                            </FormControl>
                        )}
                        {/* STEP 3: Selezione Sotto-proprietÃ  (solo se proprietÃ  selezionata) */}
                        {selectedMaterial && selectedProperty && (
                            <FormControl label={T._('Sub-property', 'Admin')}>
                                <Select
                                    options={getAvailablePropertyGroups.find((p) => p.value === selectedProperty)?.subProperties || []}
                                    value={
                                        getAvailablePropertyGroups
                                            .find((p) => p.value === selectedProperty)
                                            ?.subProperties.find((sp) => sp.value === selectedSubProperty?.value) || null
                                    }
                                    getOptionLabel={(option) => option.label}
                                    getOptionValue={(option) => option.value}
                                    onChange={(selected) => {
                                        if (selected) {
                                            setSelectedSubProperty({ value: selected.value, type: selected.type });
                                            setSelectedSubSubProperty(null);

                                            // Se la sub-property NON ha sub-sub-properties, imposta subito il propertyName
                                            if (!hasSubSubProperties(selectedProperty, selected.value)) {
                                                updatePropertyName(selected.value);
                                            }

                                            // IMPORTANTE: Quando cambio sub-property in ADD MODE (non edit),
                                            // devo sempre resettare i valori leggendoli dal materiale
                                            // Per clearCoat roughness, rileggi sempre il flag dal materiale
                                            if (selectedProperty === 'clearcoat' && selected.value === 'clearCoat_roughness_section') {
                                                if (selectedMaterial && !hasInitializedFromAction.current) {
                                                    try {
                                                        const mat = selectedMaterial as any;
                                                        if (mat.clearCoat && typeof mat.clearCoat.useRoughnessFromMainTexture !== 'undefined') {
                                                            console.log(
                                                                'ðŸ”„ Re-reading useRoughnessFromMainTexture from material:',
                                                                mat.clearCoat.useRoughnessFromMainTexture
                                                            );
                                                            setUseRoughnessFromMainTexture(mat.clearCoat.useRoughnessFromMainTexture);
                                                        }
                                                    } catch (e) {
                                                        console.warn('Could not read clearCoat.useRoughnessFromMainTexture:', e);
                                                    }
                                                }
                                            }

                                            // Reset del valore quando cambio proprietÃ  (in add mode)
                                            // In edit mode, il valore verrÃ  poi popolato dall'inizializzazione
                                            setValue('');
                                            update();
                                        }
                                    }}
                                    placeholder={T._('Select a sub-property...', 'Admin')}
                                    menuPosition='fixed'
                                />
                                {selectedProperty && (
                                    <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                                        {(() => {
                                            const selectedProp = getAvailablePropertyGroups.find((p) => p.value === selectedProperty);
                                            return selectedProp ? `${selectedProp.subProperties.length} ${T._('sub-properties available', 'Admin')}` : '';
                                        })()}
                                    </div>
                                )}
                            </FormControl>
                        )}
                        {/* STEP 4: Selezione Sub-Sub-proprietÃ  (solo se sub-property ha sub-sub-properties) */}
                        {selectedMaterial && selectedProperty && selectedSubProperty && hasSubSubProperties(selectedProperty, selectedSubProperty.value) && (
                            <FormControl label={`${selectedSubProperty.value.split('_')[0]} ${T._('sub-property', 'Admin')}`}>
                                <Select
                                    options={getSubSubProperties(selectedProperty, selectedSubProperty.value)}
                                    value={
                                        getSubSubProperties(selectedProperty, selectedSubProperty.value).find(
                                            (ssp) => ssp.value === selectedSubSubProperty?.value
                                        ) || null
                                    }
                                    getOptionLabel={(option) => option.label}
                                    getOptionValue={(option) => option.value}
                                    onChange={(selected) => {
                                        if (selected) {
                                            setSelectedSubSubProperty({ value: selected.value, type: selected.type });
                                            updatePropertyName(selected.value);
                                            setValue('');
                                            update();
                                        }
                                    }}
                                    placeholder={T._('Select a sub-sub-property...', 'Admin')}
                                    menuPosition='fixed'
                                />
                                {(() => {
                                    const subSubProps = getSubSubProperties(selectedProperty, selectedSubProperty.value);
                                    return (
                                        subSubProps.length > 0 && (
                                            <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                                                {`${subSubProps.length} ${T._('sub-sub-properties available', 'Admin')}`}
                                            </div>
                                        )
                                    );
                                })()}
                            </FormControl>
                        )}{' '}
                        {/* STEP 5: Controllo Valore (per sub-sub-property selezionata o sub-property senza sub-sub-properties) */}
                        {(selectedSubSubProperty || (selectedSubProperty && !hasSubSubProperties(selectedProperty, selectedSubProperty.value))) &&
                            (() => {
                                const currentProperty = selectedSubSubProperty || selectedSubProperty;
                                if (!currentProperty) return null;

                                // Per clearCoat roughness, passa uno state object speciale che include entrambi i valori
                                // IMPORTANTE: Se siamo nella sezione roughness, SEMPRE passa lo state object!
                                const isRoughnessProperty = selectedProperty === 'clearcoat' && selectedSubProperty?.value === 'clearCoat_roughness_section';

                                let valueToPass: string;
                                if (isRoughnessProperty) {
                                    // Per roughness, costruisci un oggetto JSON con entrambi i valori
                                    const rawTextureValue = getActionValue(parsedAction, actionType);
                                    // Se la checkbox Ã¨ abilitata O se il valore Ã¨ "false" (stringa), svuota la texture
                                    const textureValue = useRoughnessFromMainTexture || rawTextureValue === 'false' ? '' : rawTextureValue;

                                    const stateObject = {
                                        checkboxValue: useRoughnessFromMainTexture,
                                        textureValue: textureValue
                                    };
                                    valueToPass = JSON.stringify(stateObject);
                                } else {
                                    valueToPass = getActionValue(parsedAction, actionType);
                                }

                                return renderPropertyControl(
                                    {
                                        label: currentProperty.value.split('.').pop() || currentProperty.value,
                                        value: currentProperty.value,
                                        type: currentProperty.type
                                    },
                                    valueToPass,
                                    (newValue) => {
                                        // Gestione speciale per clearCoat roughness
                                        // Il detector ritorna valori prefissati con 'checkbox:' o 'texture:'
                                        if (newValue.startsWith('checkbox:')) {
                                            // Rimuovi il prefisso e controlla se c'Ã¨ il flag clearTexture
                                            const parts = newValue.replace('checkbox:', '').split('|');
                                            const actualValue = parts[0];
                                            const shouldClearTexture = parts.includes('clearTexture');

                                            // NON switchare mai la selectedSubSubProperty - mantieni sempre selezionata nella UI
                                            // Ma aggiorna il propertyName per salvare il valore corretto
                                            updatePropertyName('clearCoat.useRoughnessFromMainTexture');

                                            // Aggiorna il flag locale
                                            setUseRoughnessFromMainTexture(actualValue === 'true');
                                            setValue(actualValue);
                                            update();
                                        } else if (newValue.startsWith('texture:')) {
                                            const actualValue = newValue.replace('texture:', '');

                                            // IMPORTANTE: Quando scrivi nella texture, disabilita sempre la checkbox
                                            setUseRoughnessFromMainTexture(false);

                                            // NON switchare la selectedSubSubProperty - mantieni sempre selezionata la checkbox nella UI
                                            // Ma aggiorna il propertyName per salvare il valore della texture
                                            updatePropertyName('clearCoat_textureRoughnessTexture');

                                            setValue(actualValue);
                                            update();
                                        } else {
                                            // Caso normale, nessun prefisso
                                            setValue(newValue);
                                            update();
                                        }
                                    }
                                );
                            })()}
                    </>
                );
            case ComposerOptionActionsType.MOVE_CAMERA:
                return (
                    <>
                        <FormControl label={T._('Camera Position', 'Admin')}>
                            <Select
                                options={
                                    cameras?.map((camera) => ({
                                        value: camera.id,
                                        label: camera.name || camera.id
                                    })) ?? []
                                }
                                value={{
                                    value: parsedAction?.get('selector') || '',
                                    label: cameras?.find((m) => m.id === parsedAction?.get('selector'))?.name || parsedAction?.get('selector') || ''
                                }}
                                onChange={(selected) => {
                                    setSelector(selected?.value || '');
                                    update();
                                }}
                                menuPosition='fixed'
                            />
                        </FormControl>
                    </>
                );
            case ComposerOptionActionsType.SET_DESIGN_MATERIAL:
                return (
                    <>
                        <FormControl label={T._('Objects', 'Admin')}>
                            <Select
                                isMulti
                                options={
                                    meshes?.map((mesh) => ({
                                        value: mesh.id,
                                        label: mesh.name || mesh.id
                                    })) ?? []
                                }
                                value={
                                    parsedAction

                                        ?.get('selector')
                                        ?.split(';')
                                        .map((s) => ({ value: s, label: meshes?.find((m) => m.id === s)?.name || s })) || []
                                }
                                onChange={(selected) => {
                                    setSelector(
                                        selected
                                            .filter((s) => s.value.trim())
                                            .map((s) => s.value)
                                            .join(';')
                                    );
                                    update();
                                }}
                                menuPosition='fixed'
                            />
                            <FormControl label={T._('Material', 'Admin')}>
                                <Select
                                    options={
                                        materials?.map((material) => ({
                                            value: material.id,
                                            label: material.name || material.id
                                        })) ?? []
                                    }
                                    value={{
                                        value: (parsedAction?.get('value') as string) || '',
                                        label: materials?.find((m) => m.id === parsedAction?.get('value'))?.name || (parsedAction?.get('value') as string) || ''
                                    }}
                                    onChange={(selected) => {
                                        setValue(selected?.value || '');
                                        update();
                                    }}
                                    menuPosition='fixed'
                                />
                            </FormControl>
                        </FormControl>
                    </>
                );
            case ComposerOptionActionsType.SET_CAMERA_PIVOT:
                return (
                    <>
                        <FormControl label={T._('Camera Position', 'Admin')}>
                            <Select
                                options={
                                    cameras?.map((camera) => ({
                                        value: camera.id,
                                        label: camera.name || camera.id
                                    })) ?? []
                                }
                                value={{
                                    value: parsedAction?.get('value') || '',
                                    label: cameras?.find((m) => m.id === parsedAction?.get('value'))?.name || parsedAction?.get('value') || ''
                                }}
                                onChange={(selected) => {
                                    setValue(selected?.value || '');
                                    update();
                                }}
                                menuPosition='fixed'
                            />
                        </FormControl>
                    </>
                );
            case ComposerOptionActionsType.RESET_CAMERA_PIVOT:
                parsedAction.set('value', null);
                return <></>;
            default:
                return <>{T._('Unsupported action type selected.', 'Admin')}</>;
        }
    };

    const actionOptions = getComposerActionsSelect();
    const editor = (
        <ComposerSaveOptionContainer>
            <div>
                {T._('Do you need help?', 'Admin')}{' '}
                <Link
                    href='https://zakeke.zendesk.com/hc/en-us/articles/12934839221020-Attributes-Options-and-Actions#h_01G0MHCK9C0KKE90D04WE4SVM4'
                    target='_blank'
                    rel='noopener noreferrer'
                >
                    {T._('Read here', 'Admin')}
                </Link>
            </div>
            <FormControl label={T._('Action Type', 'Admin')}>
                <Select
                    options={actionOptions}
                    value={actionOptions.find((option) => option.value === actionType) || null}
                    isOptionDisabled={(option) => option.disabled}
                    formatOptionLabel={(option) => (
                        <ActionTypeOption $disabled={option.disabled}>
                            {getActionIcon(option.icon)}
                            <div>
                                <strong>{option.label}</strong>
                                <span>{option.description}</span>
                            </div>
                        </ActionTypeOption>
                    )}
                    onChange={(selected) => {
                        if (!selected || typeof selected.value !== 'number') return;
                        const id = selected.value || ComposerOptionActionsType.SHOW_OBJECTS;
                        setActionType(id);
                        parsedAction?.set('actionTypeID', GetActionValueFromType(id));
                        parsedAction?.set('selector', '');
                        parsedAction?.set('propertyName', '');
                        parsedAction?.set('value', '');
                        update();
                    }}
                    menuPosition='fixed'
                />
            </FormControl>
            <ValidationBox
                fullWidth
                validationKey='composerMaterialAction'
                validationLabel={T._('Material action', 'Admin')}
                validationValue={{
                    actionType,
                    selectedMaterial,
                    selectedProperty,
                    selectedSubProperty,
                    selectedSubSubProperty,
                    propertyName,
                    requiresSubSubProperty: !!selectedSubProperty && hasSubSubProperties(selectedProperty, selectedSubProperty.value),
                    currentValue: getActionValue(parsedAction, actionType)
                }}
                validate={validateMaterialAction}
                validationMessage={getMaterialActionValidationMessage}
            >
                {form()}
            </ValidationBox>
        </ComposerSaveOptionContainer>
    );

    if (embedded) {
        return (
            <InlineActionEditor>
                {editor}
                <InlineActionButtons>
                    <BasicButton $size='small' onClick={onCancel}>
                        {T._('Cancel', 'Admin')}
                    </BasicButton>
                    <PrimaryButton $size='small' onClick={handleConfirm}>
                        {T._('Save action', 'Admin')}
                    </PrimaryButton>
                </InlineActionButtons>
            </InlineActionEditor>
        );
    }

    return (
        <Dialog windowDecorator={ComposerSaveOptionDialogWindow} title={title} buttons={[{ label: T._('Confirm', 'Admin'), onClick: handleConfirm }]}>
            {editor}
        </Dialog>
    );
};

const ComposerSaveActionDialog: React.FC<ComposerSaveActionProps> = (props) => (
    <ValidationStateProvider>
        <ComposerSaveActionDialogContent {...props} />
    </ValidationStateProvider>
);

export default ComposerSaveActionDialog;
